import React, { useCallback, useRef, useState } from 'react';
import API_CONFIG from '../../apiConfig';
import { useAuth } from '../../context/AuthContext';
import { backgroundChangeExamplePresets } from './examplePresets';

interface BackgroundChangeExamplePreset {
    id: number;
    name: string;
    clothPreview: string;
}

interface ModelImageSelectionProps {
    imageUrls: string[];
    setImageUrls: React.Dispatch<React.SetStateAction<string[]>>;
    isExample?: boolean;
    onExampleSelect?: (modelImageUrl: string, modelImageId: number) => void;
}

// Use example presets for model images
const exampleModelImages = backgroundChangeExamplePresets.map((preset: BackgroundChangeExamplePreset) => ({
    id: preset.id,
    url: preset.clothPreview,
    description: preset.name
}));

// Export example handler for use in parent
export const handleExampleModelClick = async (
    url: string,
    setImageUrls: React.Dispatch<React.SetStateAction<string[]>>,
    onExampleSelect?: (modelImageUrl: string, modelImageId: number) => void
) => {
    try {
        setImageUrls([url]);
        if (onExampleSelect) {
            const exampleModelImageItem = exampleModelImages.find((item: { id: number; url: string; description: string }) => item.url === url);
            if (exampleModelImageItem) {
                onExampleSelect(url, exampleModelImageItem.id);
            }
        }
    } catch (error) {
        console.error('Error loading example image:', error);
    }
};

// Export example images for use in parent
export { exampleModelImages };

const ModelImageSelection: React.FC<ModelImageSelectionProps> = ({ imageUrls, setImageUrls, isExample = false, onExampleSelect }) => {
    const [uploading, setUploading] = useState(false);
    const [uploadStatus, setUploadStatus] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { currentUser } = useAuth();

    const hasUploadedImages = imageUrls.length > 0;

    const handleFiles = useCallback(async (files: FileList | null) => {
        if (!files || files.length === 0) return;
        setUploading(true);
        setUploadStatus(null);
        const formData = new FormData();
        Array.from(files).forEach((file) => {
            formData.append('files', file);
        });
        formData.append('userId', currentUser?.uid || 'anonymous');

        try {
            // Use background change input upload endpoint
            const response = await fetch(API_CONFIG.BACKGROUND_CHANGE_UPLOAD_INPUT_URL || API_CONFIG.AI_POSES_UPLOAD_URL, {
                method: 'POST',
                body: formData,
            });

            // Check HTTP status
            if (!response.ok) {
                setUploadStatus('Upload failed. Please try again.');
                setImageUrls([]);
                return;
            }

            const data = await response.json();

            // Check success flag and validate response
            if (data.success) {
                if (data.uploaded && Array.isArray(data.uploaded) && data.uploaded.length > 0) {
                    setImageUrls(data.uploaded.map((item: { url: string }) => item.url));
                    setUploadStatus('Upload successful!');
                } else if (data.urls && Array.isArray(data.urls) && data.urls.length > 0) {
                    setImageUrls(data.urls);
                    setUploadStatus('Upload successful!');
                } else if (data.url) {
                    setImageUrls([data.url]);
                    setUploadStatus('Upload successful!');
                } else {
                    setUploadStatus('Upload failed. No files received.');
                    setImageUrls([]);
                }
            } else {
                setUploadStatus(data.message || 'Upload failed. Please try again.');
                setImageUrls([]);
            }
        } catch (error) {
            setUploadStatus('Upload failed. Please try again.');
            setImageUrls([]);
        } finally {
            setUploading(false);
        }
    }, [currentUser?.uid, setImageUrls, setUploadStatus, setUploading]);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        handleFiles(e.dataTransfer.files);
    }, [handleFiles]);

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
    }, []);

    const handleButtonClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        handleFiles(e.target.files);
    };

    return (
        <div className="h-full w-full flex flex-col overflow-hidden">
            {/* Upload area or preview - takes full height */}
            {hasUploadedImages ? (
                <div className="bg-accent-sand rounded-lg p-2 border-2 border-dashed border-accent-taupe flex flex-col items-center justify-center relative h-full overflow-hidden">
                    {/* Cross button to close preview and allow new upload */}
                    <button
                        className="absolute top-1 right-1 text-text-description hover:text-red-500 text-xl focus:outline-none z-10"
                        onClick={() => {
                            setImageUrls([]);
                            setUploadStatus(null);
                        }}
                        aria-label="Close preview"
                    >
                        &times;
                    </button>
                    <h3 className="font-medium text-text-heading mb-1 text-xs flex-shrink-0">
                        {isExample ? 'Example Model Image' : `Uploaded Model Image${imageUrls.length > 1 ? 's' : ''}`}
                    </h3>
                    <div className="flex flex-col items-center justify-center w-full flex-1 overflow-hidden min-h-0">
                        {imageUrls.map((url, idx) => (
                            <div key={idx} className="flex justify-center items-center w-full h-full overflow-hidden">
                                <img
                                    src={url}
                                    alt={`${isExample ? 'Example' : 'Uploaded'} model preview ${idx + 1}`}
                                    className="max-w-full max-h-full object-contain rounded shadow-md bg-white"
                                />
                            </div>
                        ))}
                    </div>
                    {!isExample && uploadStatus && (
                        <p className="text-green-600 mt-1 text-xs flex-shrink-0">Upload successful!</p>
                    )}
                    {isExample && (
                        <p className="text-text-description mt-1 text-xs flex-shrink-0">This is an example image. Upload your own to customize.</p>
                    )}
                </div>
            ) : (
                <div
                    className="bg-accent-sand rounded-lg p-4 border-2 border-dashed border-accent-taupe flex flex-col items-center justify-center h-full"
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                >
                    <input
                        type="file"
                        ref={fileInputRef}
                        style={{ display: 'none' }}
                        onChange={handleFileChange}
                        accept="image/*"
                    />
                    <div className="w-12 h-12 bg-secondary-100 rounded-full flex items-center justify-center mb-3">
                        <i className="fas fa-cloud-upload-alt text-secondary-600 text-xl"></i>
                    </div>
                    <h3 className="font-medium text-text-heading mb-1 text-sm">Upload Model Images</h3>
                    <p className="text-xs text-text-description mb-3 text-center">Drag & drop images of models or click to browse</p>
                    {uploading ? (
                        <div className="flex items-center justify-center h-10">
                            <span className="inline-block w-6 h-6 border-4 border-secondary-500 border-t-transparent rounded-full animate-spin"></span>
                            <span className="ml-2 text-xs text-text-description">Uploading...</span>
                        </div>
                    ) : (
                        <button
                            className="bg-secondary-500 hover:bg-secondary-600 text-white px-4 py-1.5 rounded-lg font-medium text-sm"
                            onClick={handleButtonClick}
                            disabled={uploading}
                        >
                            Select Files
                        </button>
                    )}
                    <p className="text-xs text-text-description mt-2">JPG, PNG up to 10MB</p>
                    {uploadStatus && (
                        <p className={`mt-2 text-xs ${uploadStatus.includes('successful') ? 'text-green-600' : 'text-red-600'}`}>{uploadStatus}</p>
                    )}
                </div>
            )}
        </div>
    );
};

export default ModelImageSelection;
